import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  message,
  Popconfirm,
  Tag,
  Image,
  Typography,
  Empty,
  Spin,
  ConfigProvider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EyeOutlined
} from '@ant-design/icons';
import Layout from '../components/Layout';
import QuestionForm from '../components/QuestionForm';
import api, { getStaticImageUrl } from '../services/api';
import { Question } from '../types';
import { getApiErrorMessage } from '../utils/apiError';

const { Title, Text } = Typography;

const TestQuestions: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [test, setTest] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const uniqueQuestionCount = useMemo(() => {
    const normalizedTexts = questions
      .map((question) => (question.text || '').trim().replace(/\s+/g, ' ').toLowerCase())
      .filter(Boolean);
    return new Set(normalizedTexts).size;
  }, [questions]);

  useEffect(() => {
    if (testId) {
      fetchTestData();
      fetchQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTestData/fetchQuestions stable, testId is the trigger
  }, [testId]);

  const fetchTestData = async () => {
    try {
      const response = await api.get(`/admin/tests/${testId}`);
      setTest(response.data);
    } catch (error: any) {
      message.error('Ошибка загрузки данных теста');
      navigate('/admin/tests');
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/tests/${testId}/questions`);
      setQuestions(response.data || []);
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch (error: any) {
      message.error('Ошибка загрузки вопросов');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setFormVisible(true);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setFormVisible(true);
  };

  const handleDeleteQuestion = async (questionId: number) => {
    try {
      await api.delete(`/admin/questions/${questionId}`);
      message.success('Вопрос успешно удален');
      fetchQuestions();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка удаления вопроса'));
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingQuestion(null);
    fetchQuestions();
  };

  const handleFormCancel = () => {
    setFormVisible(false);
    setEditingQuestion(null);
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'single_choice': 'Один ответ',
      'multiple_choice': 'Множественный выбор',
      'open_answer': 'Открытый ответ',
      'matching': 'Соответствие',
      'image_identification': 'Определение по изображению'
    };
    return labels[type] || type;
  };

  const getQuestionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'single_choice': 'blue',
      'multiple_choice': 'green',
      'open_answer': 'orange',
      'matching': 'purple',
      'image_identification': 'cyan'
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: '№',
      key: 'order',
      width: 60,
      render: (_: any, _record: Question, index: number) => (
        <Text strong>{(pagination.current - 1) * pagination.pageSize + index + 1}</Text>
      )
    },
    {
      title: 'Текст вопроса',
      dataIndex: 'text',
      key: 'text',
      width: 560,
      render: (text: string) => (
        <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 180,
      render: (type: string) => (
        <Tag color={getQuestionTypeColor(type)}>
          {getQuestionTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: 'Изображение',
      key: 'image',
      width: 100,
      render: (_: any, record: Question) => {
        if (record.image_url) {
          return (
            <Image
              src={getStaticImageUrl(record.image_url)}
              alt="Question"
              width={50}
              height={50}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              preview={{
                src: getStaticImageUrl(record.image_url)
              }}
            />
          );
        }
        return <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Варианты ответов',
      key: 'answers',
      width: 150,
      render: (_: any, record: Question) => {
        if (record.type === 'open_answer') {
          return <Text type="secondary">Открытый ответ</Text>;
        }
        return (
          <Text>
            {record.answers?.length || 0} {record.answers?.length === 1 ? 'вариант' : 'вариантов'}
          </Text>
        );
      }
    },
    {
      title: 'Правильных',
      key: 'correct',
      width: 120,
      render: (_: any, record: Question) => {
        if (record.type === 'open_answer') {
          return <Text type="secondary">—</Text>;
        }
        const correctCount = record.answers?.filter(a => a.is_correct).length || 0;
        return (
          <Tag color={correctCount > 0 ? 'green' : 'red'}>
            {correctCount}
          </Tag>
        );
      }
    },
    {
      title: 'Баллы',
      dataIndex: 'points',
      key: 'points',
      width: 80,
      render: (points: number) => <Text strong>{points}</Text>
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Question) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              Modal.info({
                title: 'Детали вопроса',
                width: 800,
                content: (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Текст вопроса:</Text>
                      <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                        {record.text}
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Тип:</Text>
                      <Tag color={getQuestionTypeColor(record.type)} style={{ marginLeft: 8 }}>
                        {getQuestionTypeLabel(record.type)}
                      </Tag>
                    </div>
                    {record.image_url && (
                      <div style={{ marginBottom: 16 }}>
                        <Text strong>Изображение:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Image
                            src={getStaticImageUrl(record.image_url)}
                            alt="Question"
                            style={{ maxWidth: '100%', maxHeight: 300 }}
                          />
                        </div>
                      </div>
                    )}
                    {record.answers && record.answers.length > 0 && (
                      <div>
                        <Text strong>Варианты ответов:</Text>
                        <div style={{ marginTop: 8 }}>
                          {record.answers.map((answer, index) => (
                            <div
                              key={answer.id || index}
                              style={{
                                padding: 8,
                                marginBottom: 4,
                                background: answer.is_correct ? '#f6ffed' : '#fff',
                                border: `1px solid ${answer.is_correct ? '#b7eb8f' : '#d9d9d9'}`,
                                borderRadius: 4
                              }}
                            >
                              <Space>
                                {answer.is_correct && <Tag color="green">✓ Правильный</Tag>}
                                <Text>{answer.text}</Text>
                                {answer.image_url && (
                                  <Image
                                    src={getStaticImageUrl(answer.image_url)}
                                    alt="Answer"
                                    width={50}
                                    height={50}
                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                    preview
                                  />
                                )}
                              </Space>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              });
            }}
          >
            Просмотр
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditQuestion(record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить вопрос?"
            description="Это действие нельзя отменить"
            onConfirm={() => handleDeleteQuestion(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading && !test) {
    return (
      <Layout wide>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout wide>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/tests')}
          >
            Назад к админ-панели
          </Button>
        </Space>
      </div>

      <Card
        title={
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Управление вопросами
            </Title>
            {test && (
              <Text type="secondary" style={{ fontSize: 14 }}>
                Тест: {test.title}
              </Text>
            )}
            {questions.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Всего вопросов: {questions.length}; уникальных формулировок: {uniqueQuestionCount}
                </Text>
              </div>
            )}
          </div>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAddQuestion}
          >
            Добавить вопрос
          </Button>
        }
        style={{ marginBottom: 24, overflow: 'visible' }}
        bodyStyle={{ overflow: 'visible', padding: '24px' }}
      >
        {questions.length === 0 ? (
          <Empty
            description="Вопросы не добавлены"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddQuestion}>
              Добавить первый вопрос
            </Button>
          </Empty>
        ) : (
          <div ref={tableContainerRef} style={{ position: 'relative', overflow: 'visible' }}>
            <ConfigProvider
              getPopupContainer={(triggerNode) => {
                // Используем body для выпадающих списков, чтобы они не обрезались
                return document.body;
              }}
              dropdownMatchSelectWidth={false}
            >
              <Table
                dataSource={questions}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100', '200'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} вопросов`,
                  locale: {
                    items_per_page: ' / стр.',
                  },
                  showQuickJumper: false,
                  responsive: true,
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination({ current: 1, pageSize: size });
                  },
                }}
                scroll={{ x: 1300 }}
              />
            </ConfigProvider>
          </div>
        )}
      </Card>

      {/* Модальное окно с формой вопроса */}
      <Modal
        title={editingQuestion ? 'Редактирование вопроса' : 'Добавление вопроса'}
        open={formVisible}
        onCancel={handleFormCancel}
        footer={null}
        width={1180}
        destroyOnClose
      >
        {testId && (
          <QuestionForm
            testId={parseInt(testId)}
            initialData={editingQuestion || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default TestQuestions;
